import _ from "lodash";
import React, { useEffect, useState, useRef } from "react";
import classes from "./UserDetails.scss";
import I18nHOC from "UIComponents/I18nHOC";
import { compose } from "lib/apolloClient";
import ConditionalWrapper from "Utils/ConditionalWrapper";
import { GENERIC_TAGS_INFO } from "modules/Services";
import { Tag, Tooltip } from "@toddle-edu/ds-web";
import classNames from "classnames";
import { onEnterOrSpaceKeyDown } from "Utils";
import StudentAlerts from "AppComponents/StudentAlerts/StudentAlerts";

const styles = {
  nameStyle: {
    display: "block",
  },
};

const ConditionalTooltip = props => {
  const { name, containerClass, tooltipPlacement, style } = props;

  const ref = useRef();
  const [showTooltip, setShowTooltip] = useState(false);

  useEffect(() => {
    if (ref && ref.current) {
      const textElement = ref?.current;
      if (textElement.scrollWidth > textElement?.offsetWidth) {
        setShowTooltip(true);
      }
    }
  }, [name]);

  const containerClassStyles = { ...style, ...styles.nameStyle };

  return (
    <ConditionalWrapper
      type="wrapperAsElement"
      wrapper={<Tooltip tooltip={name} placement={tooltipPlacement} />}
      condition={showTooltip}
    >
      <div
        className={containerClass}
        ref={ref}
        dir="auto"
        style={containerClassStyles}
      >
        {name}
      </div>
    </ConditionalWrapper>
  );
};

const UserDetails = ({
  t,
  name,
  isArchived,
  email,
  viewMode,
  labelTags,
  nameClasses,
  profileTextStyle,
  statusWrapperStyle = {},
  type,
  wrapper,
  condition,
  nameWithLink = false,
  showArchivedLabel = true,
  customSubText,
  customSubTextStyle = {},
  customNameClass,
  onProfileClick = () => {},
  dsVersion,
  testId,
  dir = "auto",
  studentAlertsConfig,
}) => {
  const isDSVersion2 = _.isEqual(dsVersion, "2.0");

  const showStudentAlerts = !_.isEmpty(studentAlertsConfig);

  const { studentId = null, alertTypes = null, showAlertPopover = true } =
    studentAlertsConfig || {};

  if (viewMode === "NAME_AND_EMAIL") {
    const nameClass = classNames(
      {
        [classes.name]: !isDSVersion2,
        [classes.nameWithGlobalHeader]: isDSVersion2,
        ["text-body"]: isDSVersion2,
        [classes.nameWithLink]: nameWithLink,
        [classes.studentName]: showStudentAlerts,
      },
      customNameClass
    );

    const emailClass = classNames({
      [classes.email]: !isDSVersion2,
      [classes.emailWithGlobalHeader]: isDSVersion2,
      ["text-body-s"]: isDSVersion2,
    });

    const handleKeyDown = e => {
      onEnterOrSpaceKeyDown(e, onProfileClick);
    };

    const nameLabelClasses = classNames(
      classes.nameLabelContainer,
      customNameClass
    );

    return (
      <div className={classes.nameContainer}>
        {name ? (
          <div
            className={nameClass}
            style={profileTextStyle}
            onClick={onProfileClick}
            role="button"
            tabIndex={0}
            onKeyDown={handleKeyDown}
            data-test-id={testId ? `${testId}-nameButton` : null}
            dir={dir}
          >
            {showStudentAlerts ? (
              <div className={classes.nameWrapper}>
                <ConditionalTooltip
                  name={name}
                  containerClass={nameLabelClasses}
                  tooltipPlacement={"topRight"}
                  style={profileTextStyle}
                />
                <StudentAlerts
                  studentId={studentId}
                  alertTypes={alertTypes}
                  showAlertPopover={showAlertPopover}
                />
              </div>
            ) : (
              name
            )}
          </div>
        ) : null}
        {!isArchived ? (
          <div className={emailClass}>{email}</div>
        ) : (
          <div className={classes.rowWrapper}>
            {email ? <div className={emailClass}>{email}</div> : null}
            {email && showArchivedLabel ? (
              <span className={classes.seperator}>•</span>
            ) : null}
            {showArchivedLabel ? (
              <div className={classes.archived}>{t("common:archived")}</div>
            ) : null}
          </div>
        )}
        {!_.isEmpty(labelTags) ? (
          <div className={classes.tagsContainer}>
            {_.map(labelTags, tag => {
              return (
                <Tag
                  key={tag?.label}
                  size={"xx-small"}
                  color={_.get(
                    GENERIC_TAGS_INFO,
                    `${tag?.label}.color`,
                    "teal"
                  )}
                >
                  {tag?.label}
                </Tag>
              );
            })}
          </div>
        ) : null}
      </div>
    );
  } else if (viewMode === "NAME_ONLY") {
    const nameLabelClasses = classNames(
      classes.nameLabelContainer,
      customNameClass
    );

    return (
      <div className={classes.statusWrapper} style={statusWrapperStyle}>
        <ConditionalWrapper condition={condition} wrapper={wrapper} type={type}>
          <div className={nameClasses} dir="auto">
            {showStudentAlerts ? (
              <div className={classes.nameWrapper}>
                <ConditionalTooltip
                  name={name}
                  containerClass={nameLabelClasses}
                  tooltipPlacement={"topRight"}
                />
                <StudentAlerts
                  studentId={studentId}
                  alertTypes={alertTypes}
                  showAlertPopover={showAlertPopover}
                />
              </div>
            ) : (
              name
            )}
          </div>
        </ConditionalWrapper>
        {isArchived && showArchivedLabel ? (
          <div className={classes.archived}>{t("common:archived")}</div>
        ) : null}
      </div>
    );
  } else if (viewMode === "NAME_AND_CUSTOM_SUBTEXT") {
    const customSubTextClass = classNames({
      [classes.subText]: !isDSVersion2,
      ["text-body-s text-secondary"]: isDSVersion2,
    });

    const nameClass = classNames(
      {
        [classes.name]: !isDSVersion2,
        ["text-body"]: isDSVersion2,
        [classes.nameWithGlobalHeader]: isDSVersion2,
        [classes.studentName]: showStudentAlerts,
      },
      customNameClass
    );

    const nameLabelClasses = classNames(
      classes.nameLabelContainer,
      customNameClass
    );

    return (
      <div className={classes.nameContainer} style={statusWrapperStyle}>
        {name ? (
          <div className={nameClass} style={profileTextStyle} dir={dir}>
            {showStudentAlerts ? (
              <div className={classes.nameWrapper}>
                <ConditionalTooltip
                  name={name}
                  containerClass={nameLabelClasses}
                  tooltipPlacement={"topRight"}
                />
                <StudentAlerts
                  studentId={studentId}
                  alertTypes={alertTypes}
                  showAlertPopover={showAlertPopover}
                />
              </div>
            ) : (
              name
            )}
          </div>
        ) : null}
        {customSubText ? (
          <div
            className={customSubTextClass}
            style={customSubTextStyle}
            dir={dir}
          >
            {customSubText}
          </div>
        ) : null}
      </div>
    );
  } else return null;
};

export default compose(
  I18nHOC({
    resource: ["common"],
  })
)(UserDetails);
